import {
  toWSDateString,
  WSCard,
  WSElement,
  WSEmptyState,
  WSList,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { DataViewGrid } from "../../../shared/components/DataView";
import {
  ExtendedRequirementTableItemData,
  RequirementAdditionalData,
  RequirementContextType,
  RequirementTableItemData
} from "../types";
import { useRequirementsConfig } from "../useRequirementsConfig";
import {
  getIsCustomRequirement,
  getIsGeneralRequirement,
  getRequirementListKey
} from "../utils";

type RequirementPreviewListProps = {
  requirements: Array<ExtendedRequirementTableItemData>;
  contextType: RequirementContextType;
  hideTaxVerificationConsent?: boolean;
  getOnViewDetails: (
    item: IRequirementResponse,
    additionalData?: RequirementAdditionalData
  ) => ((item: IRequirementResponse) => void) | undefined;
};

export const ExtendedRequirementPreviewList: React.FC<
  RequirementPreviewListProps
> = ({
  requirements,
  contextType,
  getOnViewDetails,
  hideTaxVerificationConsent
}) => {
  const requirementsConfig = useRequirementsConfig({
    requirements: requirements.filter(
      requirement => "requirementId" in requirement
    ) as RequirementTableItemData[],
    contextType,
    context: {
      hideTaxVerificationConsent
    }
  });

  if (requirements.length === 0) {
    return (
      <WSEmptyState
        type="tasks"
        title="None required"
        description="Nothing required for this payee"
        hasBorder
        orientation="horizontal"
      />
    );
  }

  return (
    <WSList>
      {requirements.map((requirement, index) => {
        const isCustomRequirement = getIsCustomRequirement(requirement);
        const hasViewDetails =
          !isCustomRequirement && !getIsGeneralRequirement(requirement);

        return (
          <WSCard key={getRequirementListKey(requirement, index)}>
            <WSList gap="M">
              <WSElement>
                <WSSectionToolbar
                  title={
                    isCustomRequirement
                      ? requirement.nameCell.text || ""
                      : requirementsConfig.getNameCellProps(requirement).text ||
                        ""
                  }
                  mb="NONE"
                  button={
                    hasViewDetails
                      ? {
                          label: "View",
                          onClick: () =>
                            getOnViewDetails(
                              requirement,
                              requirementsConfig.getAdditionalData(requirement)
                            )?.(requirement)
                        }
                      : undefined
                  }
                  actions={
                    isCustomRequirement
                      ? requirement.menuActions
                      : requirementsConfig.getToolbarActions(requirement)
                  }
                />
                {!isCustomRequirement && (
                  <WSText.ParagraphXs color="gray500">
                    Updated at {toWSDateString(requirement.updatedAt)}
                  </WSText.ParagraphXs>
                )}
              </WSElement>
              <DataViewGrid
                gridColumns={3}
                data={
                  isCustomRequirement
                    ? []
                    : requirementsConfig.getRequirementBasicInformation(
                        requirement
                      ) || []
                }
              />
              {hasViewDetails
                ? requirementsConfig.getRequirementDetailsProgressWidget(
                    requirement,
                    false
                  )
                : undefined}
            </WSList>
          </WSCard>
        );
      })}
    </WSList>
  );
};
